// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Extract a string from a translatable field for the requested locale, with a
 * documented fallback chain. Safe to call on any value — never throws.
 *
 * Resolution order:
 * 1. If the field is a plain string, return it as-is (legacy shape — locale ignored).
 * 2. If the field is a locale map and contains the requested locale, return it.
 * 3. If the requested locale is BCP-47 (e.g. `'vi-VN'`, `'de-DE'`) and the map
 *    contains the language-only prefix (e.g. `'vi'`, `'de'`), return that.
 *    Pack content uses language-only codes; frontend `useLocale()` returns
 *    BCP-47 codes — this step bridges the two without forcing pack authors to
 *    enumerate every region tag.
 * 4. If the locale map contains the fallback locale (default `'en'`), return it.
 * 5. If the fallback locale itself is BCP-47, also try its language prefix.
 * 6. If the locale map contains any value at all, return the first one.
 * 7. Return `''` for null/undefined/empty/non-string-non-object input.
 *
 * @param field    The translatable value from pack content.
 * @param locale   The desired locale code (e.g. `'vi'`, `'vi-VN'`, `'de-DE'`).
 * @param fallback The fallback locale when the desired locale is missing. Defaults to `'en'`.
 * @returns The extracted string, or `''` if no string can be derived.
 *
 * @example
 *   extractLocale('Hello', 'vi') === 'Hello'                  // legacy shape, locale ignored
 *   extractLocale({en: 'Hello', vi: 'Xin chào'}, 'vi-VN') === 'Xin chào'  // BCP-47 → language prefix
 *   extractLocale({en: 'Hello', vi: 'Xin chào'}, 'vi') === 'Xin chào'
 *   extractLocale({en: 'Hello'}, 'vi-VN') === 'Hello'         // falls back to en
 *   extractLocale({de: 'Hallo'}, 'vi-VN', 'en-US') === 'Hallo' // first available value
 *   extractLocale(null, 'vi') === ''                          // safe on null
 */
export function extractLocale(field, locale, fallback = 'en') {
    // 7. null / undefined / empty
    if (field === null || field === undefined)
        return '';
    // 1. Plain string — legacy shape
    if (typeof field === 'string')
        return field;
    // Non-object inputs (numbers, booleans, arrays) — defensive: return ''
    if (typeof field !== 'object')
        return '';
    // 2. Locale map — requested locale present (exact match, e.g. 'vi-VN' → 'vi-VN')
    const map = field;
    const requested = map[locale];
    if (typeof requested === 'string')
        return requested;
    // 3. Locale map — try language-only prefix when locale is BCP-47.
    // Pack content uses 2-3 char language codes ('vi', 'de', 'fr'); frontend
    // useLocale() returns BCP-47 codes ('vi-VN', 'de-DE', 'fr-FR'). Bridge them.
    if (locale.includes('-')) {
        const langOnly = locale.split('-')[0];
        const langValue = map[langOnly];
        if (typeof langValue === 'string')
            return langValue;
    }
    // 4. Locale map — fallback locale present (exact match)
    if (locale !== fallback) {
        const fallbackValue = map[fallback];
        if (typeof fallbackValue === 'string')
            return fallbackValue;
        // 5. Locale map — fallback language-only prefix when fallback is BCP-47
        if (fallback.includes('-')) {
            const fallbackLangOnly = fallback.split('-')[0];
            const fallbackLangValue = map[fallbackLangOnly];
            if (typeof fallbackLangValue === 'string')
                return fallbackLangValue;
        }
    }
    // 6. Locale map — first available string value
    for (const value of Object.values(map)) {
        if (typeof value === 'string')
            return value;
    }
    // 7. Empty map / no string values
    return '';
}
/**
 * Type-guard: is a value already in locale-map shape?
 *
 * Useful in migration scripts and pack-author tooling to detect already-migrated
 * fields and skip them (idempotency).
 */
export function isLocaleMap(field) {
    if (field === null || field === undefined)
        return false;
    if (typeof field !== 'object')
        return false;
    if (Array.isArray(field))
        return false;
    const obj = field;
    const keys = Object.keys(obj);
    if (keys.length === 0)
        return false;
    // All keys must look like locale codes (2-3 chars, alphabetic) and all values must be strings
    return (keys.every((k) => /^[a-z]{2,3}$/i.test(k)) &&
        Object.values(obj).every((v) => typeof v === 'string'));
}
/**
 * fix-sprint-20260904-2130-duplicated-implementations F4 — the ONE guard for a
 * pack-declared bilingual text (`BilingualText`, `{ en, vi }`).
 *
 * Six backend modules each wrote a byte-identical local `isBilingual`
 * (publishing, issued-records, sector-licensing, data-services,
 * credit-information-supply, marketing-communications) — the class REUSE.md's
 * `extractLocale` entry already recorded for the spread adapters. A question
 * about the guard had to be answered six times or not at all; this is where it
 * is answered once.
 *
 * **An empty half is NOT a declared label.** Each copy accepted
 * `{ en: '', vi: '' }`, so a pack record carrying an empty label "declared" its
 * kind's label and, because the readers take the first record that declares
 * one, blocked a later record's real label from ever showing (Sprint 62.19.0
 * gate pass 4, recorded and never answered). Both halves must be non-empty
 * after trimming. Measured over `packs/**` on 2026-10-20: no pack authors an
 * empty or half-empty `{ en, vi }`, so the ruling changes no shipped label.
 * The shape is the closed `{ en, vi }`; extra locale keys are allowed.
 */
export function isBilingualText(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return false;
    const v = value;
    return (typeof v.en === 'string' &&
        typeof v.vi === 'string' &&
        v.en.trim().length > 0 &&
        v.vi.trim().length > 0);
}
//# sourceMappingURL=locale-extraction.js.map